'use client';

import { SectLabel, SectH2, GlassCard } from '@/components/landing/v4-shared';
import { V4EnterGrid } from '@/components/landing/V4EnterGrid';
import { AeoHubLink } from '@/components/ai-receptionist/aeo-instrumentation';
import {
  AI_SEARCH_PLATFORMS,
} from '@/lib/marketing/messaging-bank';

const SPOKE_LINKS = [
  { href: '/simple-setup', label: 'Simple Setup — forward one number, AI answers every call', eyebrow: 'Pillar 1' },
  { href: '/ai-business-page', label: 'AI Business Page — be recommended by ChatGPT, Gemini, Perplexity', eyebrow: 'Pillar 2' },
  { href: '/best/alternatives/smith-ai', label: 'BizRnR and Smith.ai — reviewed alternatives', eyebrow: 'Comparison' },
  { href: '/best/alternatives/ruby-receptionists', label: 'BizRnR and Ruby Receptionists — reviewed alternatives', eyebrow: 'Comparison' },
  { href: '/best/ai-receptionist/law-firms', label: 'AI receptionist for law firms — sourced legal-intake guidance', eyebrow: 'Legal evidence' },
] as const;

const ANSWER_ENGINES_INTRO =
  `In 2026, prospects increasingly find local businesses by asking ${AI_SEARCH_PLATFORMS.join(', ')} — not only by searching Google. BizRnR addresses both sides of that shift: an AI voice receptionist that answers every call live, plus an AI Business Page with crawlable structured data and assistant-ready context. The two pillars close the loop — discovery sends prospects to you, and your AI voice receptionist converts them on the phone.`;

export function AnswerEnginesSection() {
  return (
    <section aria-labelledby="answer-engines-heading" className="max-w-[960px] mx-auto px-6" style={{ marginBottom: 56 }}>
      <GlassCard accent="amber" padding={28} radius={20}>
        <SectLabel color="var(--amber-glow)">Answer engines</SectLabel>
        <h2
          id="answer-engines-heading"
          style={{
            fontFamily: 'var(--font-geist-sans)',
            fontSize: 'clamp(24px, 3vw, 36px)',
            fontWeight: 500,
            color: 'var(--paper)',
            letterSpacing: '-0.02em',
            margin: '0 0 14px',
          }}
        >
          AI Voice Receptionist Meets AI Discovery
        </h2>
        <p className="speakable-answer" style={{ color: 'var(--dim)', lineHeight: 1.65, fontSize: 16, margin: '0 0 16px' }}>
          {ANSWER_ENGINES_INTRO}
        </p>
        <ul
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: 8,
            listStyle: 'none',
            padding: 0,
            margin: 0,
          }}
        >
          {AI_SEARCH_PLATFORMS.map((p) => (
            <li
              key={p}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                color: 'var(--dim)',
                fontSize: 14,
              }}
            >
              <span aria-hidden="true" style={{ color: 'var(--amber-glow)' }}>
                &bull;
              </span>
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </GlassCard>
    </section>
  );
}

export function ClusterSection({
  sourcePage = '/ai-voice-receptionist',
}: {
  sourcePage?: string;
} = {}) {
  return (
    <section aria-labelledby="cluster-heading" className="max-w-[960px] mx-auto px-6" style={{ marginBottom: 56 }}>
      <SectLabel>Cluster</SectLabel>
      <SectH2 id="cluster-heading" style={{ fontSize: 'clamp(28px, 3.6vw, 44px)' }}>
        Explore the AI Voice Receptionist Ecosystem
      </SectH2>
      <V4EnterGrid className="grid sm:grid-cols-2 gap-4 mt-6 v4-enter v4-stagger">
        {SPOKE_LINKS.map((l) => (
          <AeoHubLink
            key={l.href}
            href={l.href}
            sourcePage={sourcePage}
            anchorText={l.label}
            className="v4-card v4-card--cyan v4-card--hoverable"
            style={{ display: 'block', borderRadius: 16, padding: '20px 22px', textDecoration: 'none' }}
          >
            <p style={{ fontFamily: 'var(--font-geist-mono)', fontSize: 11, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--cyan-glow)', margin: '0 0 6px' }}>
              {l.eyebrow}
            </p>
            <p style={{ color: 'var(--paper)', fontWeight: 500, fontSize: 15, margin: 0, lineHeight: 1.4 }}>
              {l.label}
            </p>
          </AeoHubLink>
        ))}
      </V4EnterGrid>
    </section>
  );
}
